import orderBy from "lodash.orderby";
import { Copy, Globe } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { VariationDto } from "@intelligems/ig-types/dtos/variations";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { Stack } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { selectedExperienceState } from "@src/store/slices/experiences";

import { CmdKState, SearchOption } from "./types";
import { tokenizedMatches } from "./utils";

const openInTab = (label: string) => {
  return `Open in a new tab → ${label}`;
};

const copyPreviewLink = (label: string) => {
  return `Copy Preview link → ${label}`;
};

const copyId = (name: string) => {
  return `Copy Id → ${name}`;
};

const copyToClipboard = async (value: string) => {
  await navigator.clipboard.writeText(value);
  toast.success("Copied!");
};

interface getOptionProps {
  groupLabel: string;
  variation: VariationDto;
  secondaryAction: boolean;
  generatePreviewLink: (v: VariationDto) => string;
}

const getTestGroupOption = ({
  groupLabel,
  variation,
  secondaryAction,
  generatePreviewLink,
}: getOptionProps): [string, SearchOption] => {
  const label = !secondaryAction
    ? copyPreviewLink(variation.name)
    : openInTab(variation.name);
  return [
    label,
    {
      group: groupLabel,
      render: () => {
        const Icon = !secondaryAction ? (
          <Copy size={20} />
        ) : (
          <Globe size={20} />
        );

        return (
          <Stack direction={"row"} alignItems={"center"}>
            {Icon}
            <IgTypography marginLeft={2}>{label}</IgTypography>
          </Stack>
        );
      },
      select: () => {
        const previewLink = generatePreviewLink(variation);
        if (!secondaryAction) {
          void copyToClipboard(previewLink);
        } else {
          window.open(previewLink, "_blank");
        }
      },
    },
  ];
};

export const getTestGroupIdOption = ({
  groupLabel,
  variation,
}: getOptionProps): [string, SearchOption] => {
  const label = copyId(variation.name);
  return [
    label,
    {
      group: groupLabel,
      render: () => {
        return (
          <>
            <Copy size={20} />
            <IgTypography marginLeft={2}>{label}</IgTypography>
          </>
        );
      },
      select: () => {
        void copyToClipboard(variation.id);
      },
    },
  ];
};

export const useExperienceInfoOptions = (
  state: CmdKState,
  query?: string,
  limit?: number
): SearchOption[] => {
  const secondaryAction = !!state.secondaryAction;
  const { data, isSuccess: orgSuccess } = useGetOrg();
  const { storedExperience: experience } = useSelector(selectedExperienceState);
  if (!experience || !orgSuccess) {
    return [];
  }

  const generatePreviewLink = (variation: VariationDto): string => {
    let domain = data.org.domain ?? data.org.shopId;
    if (!domain?.startsWith("https://")) {
      domain = `https://${domain}`;
    }

    return `${domain}/?ig-preview=${experience.id}&igTg=${variation.id}`;
  };

  const userFacingTestType = officialCategoryName(experience.category, true);
  const groupLabel = experience.name
    ? `${userFacingTestType}: ${experience.name}`
    : `This ${userFacingTestType}`;

  const options: [string, SearchOption][] = orderBy(
    experience.variations,
    (_) => _.order
  ).flatMap((variation) => {
    const props = {
      groupLabel,
      variation,
      secondaryAction,
      generatePreviewLink,
    };

    return [getTestGroupOption(props), getTestGroupIdOption(props)];
  });

  let filtered: SearchOption[];
  if (query) {
    const lower = query.toLowerCase();
    filtered = options
      .filter(([label]) => tokenizedMatches(lower, label.toLowerCase()))
      .map(([, option]) => option);
  } else {
    filtered = options.map(([_, option]) => option);
  }

  return filtered.slice(0, limit);
};
